import SentryWrap from "components/Sentry";
import React, { memo } from "react";
import { formatTextForRemoveUnderScore } from "../helper";
import * as Sentry from '@sentry/react';

const FillBlanksPreview = ({
  text,
  highlights,
  selected_answer = [],
  isAssignmentReview = false,
  isGradePreview = false,
  isStudent = false,
  questionPreview = false,
  fromAnswer,
  neutral = false,
}) => {
  const stripHtmlTags = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || "";
    return text.replace(/[\u200B-\u200D\uFEFF]/g, '');
  };

  const hasHtml = /<[^>]+>/.test(text);
  const plainText = hasHtml ? stripHtmlTags(text) : text;

  // Trims any trailing overlap between beforeText and the highlight word.
  // Handles off-by-one errors where highlight.start is 1 position too late,
  // causing the last character of the highlighted word to appear in beforeText.
  // e.g. beforeText="The horse is c", highlightText="caballo" → "The horse is "
  const trimBeforeOverlap = (beforeText, highlightText) => {
    if (!beforeText || !highlightText) return beforeText;
    // Check if the tail of beforeText is a prefix of highlightText
    for (let overlap = Math.min(beforeText.length, highlightText.length); overlap > 0; overlap--) {
      if (
        beforeText.slice(-overlap) === highlightText.slice(0, overlap) &&
        // Only trim if beforeText doesn't actually end with a full word that
        // happens to share chars — require the overlap is at a word boundary
        // or the full beforeText tail matches a prefix
        overlap <= highlightText.length
      ) {
        return beforeText.slice(0, -overlap);
      }
    }
    return beforeText;
  };

  const generateBlanksText = (text, highlights, htmlText) => {
    try {
      let result = [];
      let currentIndex = 0;

      const sortedHighlights = highlights
        ?.flat()
        ?.sort((a, b) => a.start - b.start);

      if (sortedHighlights && sortedHighlights.length > 0) {
        sortedHighlights.forEach((highlight, index) => {
          const currentAnswer = selected_answer[index];

          if (highlight.start > currentIndex) {
            let beforeText = text.slice(currentIndex, highlight.start);

            // FIX: trim any trailing overlap caused by off-by-one in highlight.start
            // e.g. "The horse is c" + highlight "caballo" → trim trailing "c"
            if (highlight.text) {
              beforeText = trimBeforeOverlap(beforeText, highlight.text);
            }

            result.push(beforeText);
          }

          if (neutral) {
            result.push(
              <span key={`highlight-${index}`} className="">
                {formatTextForRemoveUnderScore(currentAnswer?.answer)}
              </span>
            );
          } else {
            result.push(
              <span
                key={`highlight-${index}`}
                className={`fill-in-the-blanks-preview ${
                  fromAnswer
                    ? "showAnswerTxt"
                    : !isAssignmentReview
                    ? currentAnswer?.is_correct
                      ? "fill-blanks-green"
                      : "fill-blanks-red"
                    : `fill-blanks-blue ${isStudent ? "student-fill-none" : ""}`
                }`}
              >
                {isAssignmentReview
                  ? formatTextForRemoveUnderScore(currentAnswer?.text)
                  : formatTextForRemoveUnderScore(currentAnswer?.answer)}
              </span>
            );
          }

          currentIndex = highlight.end;
        });
      }

      if (currentIndex < text.length) {
        result.push(text.slice(currentIndex));
      }

      return result;
    } catch (error) {
      Sentry.captureException(`560 Error in generateBlanksText: ${error}`);
      console.log(error);
    }
  };

  const insertHighlightsInHtml = (htmlText, plainText, highlights) => {
    if (!highlights || highlights.length === 0 || !Array.isArray(highlights.flat()) || highlights.flat().length === 0) {
      return htmlText;
    }

    try {
      const flatHighlights = highlights.flat();
      const sortedHighlights = flatHighlights
        .map((highlight, originalIndex) => ({ ...highlight, originalIndex }))
        .sort((a, b) => b.start - a.start);

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlText;

      const findAndReplaceText = (node, plainIndex = 0) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const originalNodeText = node.textContent || '';
          const normalizedNodeText = originalNodeText.replace(/[\u200B-\u200D\uFEFF]/g, '');
          const nodeStart = plainIndex;
          const nodeEnd = plainIndex + normalizedNodeText.length;

          const highlightsInNode = [];
          for (let i = sortedHighlights.length - 1; i >= 0; i--) {
            const highlight = sortedHighlights[i];
            if (highlight.start < nodeEnd && highlight.end > nodeStart) {
              highlightsInNode.push({ highlight, sortedIndex: i });
            }
          }

          if (highlightsInNode.length > 0) {
            highlightsInNode.sort((a, b) => a.highlight.start - b.highlight.start);

            const fragment = document.createDocumentFragment();
            let currentNormalizedPos = 0;
            let currentOriginalPos = 0;
            const indicesToRemove = [];

            for (const { highlight, sortedIndex } of highlightsInNode) {
              let relativeStart = Math.max(0, highlight.start - nodeStart);
              let relativeEnd = Math.min(normalizedNodeText.length, highlight.end - nodeStart);

              const expectedText = highlight.text
                ? highlight.text.replace(/[\u200B-\u200D\uFEFF]/g, '')
                : null;

              if (expectedText) {
                const actualTextAtPosition = normalizedNodeText.substring(relativeStart, relativeEnd);
                if (actualTextAtPosition !== expectedText) {
                  // FIX: search window starts BEFORE relativeStart (not clamped by
                  // currentNormalizedPos) so off-by-one errors in start/end are caught.
                  const searchStart = Math.max(0, relativeStart - expectedText.length - 1);
                  const searchEnd = Math.min(normalizedNodeText.length, relativeEnd + expectedText.length + 2);
                  const searchArea = normalizedNodeText.substring(searchStart, searchEnd);
                  const foundIndex = searchArea.indexOf(expectedText);

                  if (foundIndex !== -1) {
                    relativeStart = searchStart + foundIndex;
                    relativeEnd = relativeStart + expectedText.length;
                  }
                }
              }

              if (relativeStart > currentNormalizedPos) {
                const normalizedBeforeLength = relativeStart - currentNormalizedPos;
                let normalizedCount = 0;
                let beforeTextEnd = currentOriginalPos;

                for (let i = currentOriginalPos; i < originalNodeText.length; i++) {
                  const char = originalNodeText[i];
                  const isZeroWidth = /[\u200B-\u200D\uFEFF]/.test(char);
                  if (!isZeroWidth) {
                    if (normalizedCount >= normalizedBeforeLength) break;
                    normalizedCount++;
                  }
                  beforeTextEnd = i + 1;
                }

                if (beforeTextEnd > currentOriginalPos) {
                  const beforeText = originalNodeText.substring(currentOriginalPos, beforeTextEnd);
                  if (beforeText) fragment.appendChild(document.createTextNode(beforeText));
                  currentOriginalPos = beforeTextEnd;
                }
                currentNormalizedPos = relativeStart;
              }

              const normalizedHighlightLength = relativeEnd - relativeStart;
              let normalizedCount = 0;
              let highlightTextEnd = currentOriginalPos;

              for (let i = currentOriginalPos; i < originalNodeText.length; i++) {
                const char = originalNodeText[i];
                const isZeroWidth = /[\u200B-\u200D\uFEFF]/.test(char);
                if (!isZeroWidth) {
                  if (normalizedCount >= normalizedHighlightLength) break;
                  normalizedCount++;
                }
                highlightTextEnd = i + 1;
              }

              currentOriginalPos = highlightTextEnd;
              currentNormalizedPos = relativeEnd;

              const currentAnswer = selected_answer[highlight.originalIndex];
              const answerText = formatTextForRemoveUnderScore(
                currentAnswer?.text || currentAnswer?.answer || ''
              );

              const highlightSpan = document.createElement("span");
              highlightSpan.className = "fill-in-the-blanks-preview fill-blanks-blue";
              highlightSpan.style.cssText =
                "background-color: #b3d9ff; padding: 2px 4px; border-radius: 3px; display: inline-block;";
              highlightSpan.textContent = answerText;
              fragment.appendChild(highlightSpan);

              indicesToRemove.push(sortedIndex);
            }

            if (currentNormalizedPos < normalizedNodeText.length) {
              const normalizedAfterLength = normalizedNodeText.length - currentNormalizedPos;
              let normalizedCount = 0;
              let afterTextEnd = currentOriginalPos;

              for (let i = currentOriginalPos; i < originalNodeText.length && normalizedCount < normalizedAfterLength; i++) {
                const char = originalNodeText[i];
                if (!/[\u200B-\u200D\uFEFF]/.test(char)) normalizedCount++;
                afterTextEnd = i + 1;
              }

              const afterText = originalNodeText.substring(currentOriginalPos, afterTextEnd);
              if (afterText) fragment.appendChild(document.createTextNode(afterText));
            }

            indicesToRemove.sort((a, b) => b - a);
            for (const index of indicesToRemove) sortedHighlights.splice(index, 1);

            node.parentNode.replaceChild(fragment, node);
            return nodeEnd;
          }

          return nodeEnd;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          let currentIndex = plainIndex;
          const children = Array.from(node.childNodes);
          for (let child of children) {
            currentIndex = findAndReplaceText(child, currentIndex);
          }
          return currentIndex;
        }

        return plainIndex;
      };

      findAndReplaceText(tempDiv);
      return tempDiv.innerHTML;
    } catch (error) {
      Sentry.captureException(`561 Error in insertHighlightsInHtml: ${error}`);
      return htmlText;
    }
  };

  const renderHtmlWithHighlights = () => {
    if (!highlights || highlights.length === 0 || !Array.isArray(highlights.flat()) || highlights.flat().length === 0) {
      return <span dangerouslySetInnerHTML={{ __html: text || '' }} />;
    }
    const htmlWithHighlights = insertHighlightsInHtml(text, plainText, highlights);
    return <span dangerouslySetInnerHTML={{ __html: htmlWithHighlights || text || '' }} />;
  };

  const processedText =
    hasHtml && isAssignmentReview
      ? renderHtmlWithHighlights()
      : generateBlanksText(plainText, highlights, text);

  return (
    <SentryWrap>
      <div
        className={`${isAssignmentReview ? "fill-in-the-blanks-preview-text" : ""} ${
          isGradePreview ? "fill-in-the-blanks-assignment-review" : ""
        }`}
        style={{ fontSize: "16px", lineHeight: "31px" }}
      >
        <style>{`
          .fill-in-the-blanks-preview-text ol, .fill-in-the-blanks-preview-text ul {
            padding-left: 30px;
            margin: 10px 0;
          }
          .fill-in-the-blanks-preview-text ol { list-style-type: decimal; }
          .fill-in-the-blanks-preview-text ul { list-style-type: disc; }
          .fill-in-the-blanks-preview-text li { margin: 5px 0; line-height: 1.5; }
          .fill-in-the-blanks-preview-text p { margin: 10px 0; }
        `}</style>
        {!isStudent && questionPreview ? (
          <div className="flex items-center gap-10px flex-wrap">
            {highlights?.map((data) => (
              <span className="fill-blanks-blue fill-in-the-blanks-preview">
                {formatTextForRemoveUnderScore(data?.text)}
              </span>
            ))}
          </div>
        ) : (
          processedText
        )}
      </div>
    </SentryWrap>
  );
};

export default memo(FillBlanksPreview);